import Head from 'next/head';
import { useTranslation } from 'react-i18next';
import { getLocalizedUrlsFromPath, localizeUrl } from 'lib/Utilities';
import Header from 'components/Header';
import Footer from 'components/Footer';
import { colors, screen, fonts } from 'components/styles/variables';
// assets
import ogDefaultLogo from 'public/static/images/social_media/og_default_placeholder.png';
import { ReactNode } from 'react';

import AuthDialog from 'components/Auth/Dialog';
import Alerts from 'components/Alerts';
import { LOCK_SCREEN_CLASS_NAME } from 'lib/hooks/useLockedScreen';
import { useRouter } from 'next/router';
import config from 'lib/config';
import { LANGUAGE_TO_LANGUAGE_WITH_COUNTRY_MAP } from 'lib/i18next';
import WebSiteStructuredData from './WebSiteStructuredData';
import WebPageStructuredData from './WebPageStructuredData';

const BASE_AMP_URL = 'https://amp.autolist.com';

interface LayoutProps {
  alternateAndroidUrl?: string;
  ampEnabled?: boolean;
  indexed?: boolean;
  title: string;
  description: string;
  subNav?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  id?: string;
  includeAds?: boolean;
  isVdp?: boolean;
}

function getRobotsContent(indexed: boolean): string {
  let robotsTag = 'NOINDEX';
  if (indexed) {
    robotsTag = 'INDEX';
  }
  return `${robotsTag}, FOLLOW`;
}

const Layout = ({
  alternateAndroidUrl,
  ampEnabled = false,
  indexed = true,
  title,
  description,
  subNav,
  footer,
  children,
  id,
  includeAds = true,
  isVdp = false,
}: LayoutProps): JSX.Element => {
  const { asPath, locale } = useRouter();
  const { i18n } = useTranslation();
  const robotsContent = getRobotsContent(indexed);
  const urls = getLocalizedUrlsFromPath(asPath, config.host);

  return (
    <>
      <Head>
        <title>{title}</title>
        {ampEnabled && <link rel="amphtml" href={`${localizeUrl(asPath, BASE_AMP_URL, locale)}`} />}
        <meta content="app-id=com.autolist.autolist" name="google-play-app" />
        <meta content="app-id=B013TNEL8Q" name="kindle-fire-app" />
        <meta content="app-id=1058636900" name="apple-itunes-app" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <link rel="alternate" hrefLang="x-default" href={`https://${urls.en}`} />
        <link
          rel="alternate"
          hrefLang={LANGUAGE_TO_LANGUAGE_WITH_COUNTRY_MAP.get('en')}
          href={`https://${urls.en}`}
        />
        <link
          rel="alternate"
          hrefLang={LANGUAGE_TO_LANGUAGE_WITH_COUNTRY_MAP.get('es')}
          href={`https://${urls.es}`}
        />
        {alternateAndroidUrl && <link href={alternateAndroidUrl} rel="alternate" />}
        <meta
          content="width=device-width, initial-scale=1.0, user-scalable=yes, minimum-scale=1.0, maximum-scale=2.0"
          name="viewport"
        />
        <meta content={description} name="description" />
        <meta content="1386838238233125" property="fb:app_id" />
        <meta content={title} property="twitter:title" />
        <meta content={description} property="twitter:description" />
        <meta name="ROBOTS" content={robotsContent} key="robots" />
        <meta content={`https://${config.host}/${asPath}`} property="og:url" key="og:url" />
        <meta content="Autolist" property="og:site_name" key="og:site_name" />
        <meta content={i18n.language} property="og:locale" key="og:locale" />
        <meta content={title} property="og:title" key="og:title" />
        <meta content={ogDefaultLogo} property="og:image" key="og:image" />
        <meta content="website" property="og:type" key="og:type" />
        <meta content={description} property="og:description" key="og:description" />
      </Head>
      <div id={id}>
        <Header url={asPath} subNav={subNav} includeAds={includeAds} isVdp={isVdp} />
        <main className="container main">{children}</main>
        {footer || <Footer />}
      </div>
      <Alerts />
      <AuthDialog />
      <style jsx>{`
        .main {
          width: 100%;
          display: flex;
          flex-direction: column;
          padding: 0;
        }

        @media (max-width: ${screen.smMin}) {
          .main {
            /* Bootstrap override */
            padding: 0;
          }
        }
      `}</style>
      <style jsx global>
        {`
          html {
            overflow-x: hidden;
          }

          body {
            font-family: ${fonts.default};
            font-size: 16px;
            font-weight: 100;
            padding-right: inherit;
          }

          a {
            color: ${colors.ALR1};
            font-size: 15px;
            font-weight: 400;
            font-family: ${fonts.default};
            outline: none;
          }

          a.large {
            font-size: 19px !important;
            line-height: 25px !important;
          }

          a:hover,
          a:focus,
          a:active {
            text-decoration: none;
            color: red;
          }

          button {
            outline: none;
          }

          .h1 {
            margin: 0;
            padding: 0;
            font-size: 43px;
            font-weight: bold;
            line-height: 40px;
          }

          .h2 {
            margin: 0;
            padding: 0;
            font-size: 33px;
            font-weight: bold;
            line-height: 25px;
          }

          .h3 {
            margin: 0;
            padding: 0;
            font-size: 24px;
            font-weight: bold;
            line-height: 22px;
          }

          .row.row--small-gutters > [class*='col-'] {
            padding-left: 15px !important;
            padding-right: 15px !important;
          }

          @media (max-width: ${screen.xsMax}) {
            .row.row--small-gutters-xs {
              margin-left: 0px !important;
              margin-right: 0px !important;
            }

            .row.row--small-gutters-xs > [class*='col-'] {
              padding-left: 15px !important;
              padding-right: 15px !important;
            }
          }

          @media (min-width: ${screen.smMin}) {
            .row.row--small-gutters-gtxs > [class*='col-'] {
              padding-left: 15px !important;
              padding-right: 15px !important;
            }
          }

          @media (max-width: ${screen.xsMax}) {
            .row.row--no-gutters-xs {
              margin-left: 0px !important;
              margin-right: 0px !important;
            }

            .row.row--no-gutters-xs > [class*='col-'] {
              padding-left: 0px !important;
              padding-right: 0px !important;
            }
          }

          .AL-button {
            user-select: none;
            text-align: center;
            text-decoration: none;
            display: inline-block;
            background-color: ${colors.ALR1};
            color: white;
            padding: 20px;
            font-size: 18px;
            font-weight: 600;
            cursor: pointer;
            border: 0;
            border-radius: 50px;
            -webkit-tap-highlight-color: rgba(250, 250, 250, 0.5);
            transition: 1s all ease;
          }

          .AL-button:active,
          .AL-button:hover,
          .AL-button:visited,
          .AL-button:focus {
            color: white;
          }

          .AL-button--medium {
            width: 80%;
          }

          .AL-link {
            display: inline;
            color: ${colors.ALR1};
            font-size: 15px;
            font-weight: 400;
            font-family: ${fonts.default};
            padding: 0;
            margin: 0;
            text-decoration: none;
            border: none;
            background-color: transparent;
          }

          /*
          Removes a container that adds undesireable white space in the footer,
          the container is injected by 3rd party ads :/
        */
          body > img[width='1'] {
            display: none;
          }

          @media (min-width: ${screen.smMax}) {
            html.${LOCK_SCREEN_CLASS_NAME} body {
              overflow: visible;
            }
          }

          html.${LOCK_SCREEN_CLASS_NAME} {
            height: 100%;
            width: 100%;
            overflow: hidden;
          }

          html.${LOCK_SCREEN_CLASS_NAME}.scrolling-carousel {
            overflow-y: visible;
            overflow-x: visible;
          }
        `}
      </style>
      <WebPageStructuredData
        {...{
          indexed,
          title,
          description,
          host: config.host,
          path: asPath,
          subNav,
          footer,
          children,
          id,
        }}
      />
      <WebSiteStructuredData />
    </>
  );
};

export default Layout;
